import { Component, Inject, OnInit } from '@angular/core';
import { DarkModeService } from './core/services/dark-mode.service';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoaderComponent } from './shared/presentationals/loader/loader.component';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import DeviceHelper from './core/helpers/device.helper';
import { DOCUMENT } from '@angular/common';
import { AuthenticationService } from './core/services/authentication.service';
import { filter, map, switchMap } from 'rxjs';
import { CaseApiService, CompanyApiService } from './core/services/api-services';
import { TitleService } from './core/services/title.service';
import { ThemeService } from './core/services/theme.service';
import Color from './core/constants/color';
import { NavigationStateKeys } from './core/constants/navigation-state-keys';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { InactivityService } from './core/services/inactivity.service';

// Use local script to avoid CSP violations
(window as any).pdfWorkerSrc = 'assets/scripts/pdf.worker.min.js';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [LoaderComponent, RouterOutlet]
})
export class AppComponent implements OnInit {
  constructor(private readonly darkModeService: DarkModeService,
    private readonly translateService: TranslateService,
    private readonly authenticationService: AuthenticationService,
    private readonly caseApiService: CaseApiService,
    private readonly titleService: TitleService,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly themeService: ThemeService,
    private readonly companyApiService: CompanyApiService,
    private readonly inactivityService: InactivityService,
    @Inject(DOCUMENT) private readonly document: Document,
    private swUpdate: SwUpdate) {
  }

  ngOnInit(): void {
    this.themeService.apply(Color.defautThemeColor);
    this.inactivityService.initialize();

    if (this.authenticationService.isAuthenticated()) {
      this.companyApiService.getCompanyColor().subscribe({
        next: (color) => {
          const validColor = color && color.trim() !== '' ? color : Color.defautThemeColor;
          this.themeService.apply(validColor);
        },
        error: () => {
          this.themeService.apply(Color.defautThemeColor);
        }
      });
    }

    this.darkModeService.initialize();

    // Change language of date picker
    this.translateService.onLangChange.subscribe(() => {
      this.dateAdapter.setLocale(this.translateService.currentLang);
    });

    if (DeviceHelper.isMobileDevice()) {
      this.document.body.classList.add('mobile-device');
    }

    // Load protected translations once user is authenticated
    const authenticatedSubscription = this.authenticationService.authenticated$
      .subscribe(model => {
        if (model) {
          if (!this.authenticationService.getIsTokenRestored()) {
            for (let lang of this.translateService.langs) {
              this.translateService.reloadLang(lang)
                .subscribe(() => {
                  authenticatedSubscription.unsubscribe();
                });
            }
          }
        }
      });

    // Another tab logged out: show the login page in this tab as well
    this.authenticationService.remoteLogout$.subscribe(() => {
      if (this.router.url.startsWith('/authentication')) {
        return;
      }

      this.router.navigateByUrl(
        this.authenticationService.getLoginUrl(this.router.url),
        { state: { ignoreDirtyCheck: true, [NavigationStateKeys.remoteLogout]: true } });
    });

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY')
        )
        .subscribe(() => {
          window.location.reload();
        });
    }

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route?.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        switchMap(() => {
          const url = this.router.url;

          const match = url.match(/\/case-management\/cases\/detail\/([^\/]+)/);

          if (match && match[1]) {
            const caseId = match[1];
            return this.caseApiService.get(caseId);
          }

          return new Promise(resolve => resolve({ displayName: null })) as any;
        })
      )
      .subscribe((data: any) => {
        if (data?.personFirstName || data?.personLastName) {
          const nameParts = [data?.personFirstName, data?.personLastName].filter(part => part);
          this.titleService.setTitle(nameParts.join(' '));
        } else {
          this.titleService.setTitle('AKTAS Fallführung');
        }
      });
  }
}
